import React, {
  useState, useEffect, useRef, useCallback, memo
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Trash2, Mic, MicOff, Video, VideoOff,
  FileText, ChevronLeft, Wand2, Square, Play,
  Pause, Volume2, Hash, List, CheckSquare, Code,
  Search, Tag, Sparkles, Loader2, X
} from 'lucide-react';
import { useBlockNotes, createNewNote } from '../../hooks/useBlockNotes';
import { useScrollPosition } from '../../hooks/useScrollPosition';

/* ─── Gemini AI helper ──────────────────────────────────────────── */
const GEMINI_URL =
  'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-lite:generateContent';

async function getAICompletion(text, apiKey) {
  if (!apiKey || !text.trim()) return '';
  try {
    const res = await fetch(`${GEMINI_URL}?key=${apiKey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: `Tu es un assistant d'écriture pour une application ADHD. L'utilisateur écrit une note en français. Continue sa phrase de façon courte (max 15 mots), naturelle et pertinente. Réponds UNIQUEMENT avec la suite du texte, sans guillemets ni explication.\n\nTexte actuel : "${text}"\n\nSuite :`
          }]
        }],
        generationConfig: { maxOutputTokens: 60, temperature: 0.7 }
      })
    });
    if (!res.ok) return '';
    const data = await res.json();
    return data?.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || '';
  } catch {
    return '';
  }
}

/* ─── Block types ───────────────────────────────────────────────── */
const BLOCK_TYPES = {
  paragraph: { label: 'Texte', icon: FileText, placeholder: 'Écrire quelque chose...' },
  heading1:  { label: 'Titre 1', icon: Hash, placeholder: 'Titre principal' },
  heading2:  { label: 'Titre 2', icon: Hash, placeholder: 'Sous-titre' },
  bullet:    { label: 'Liste', icon: List, placeholder: 'Élément de liste' },
  checklist: { label: 'Case à cocher', icon: CheckSquare, placeholder: 'Tâche à faire' },
  code:      { label: 'Code', icon: Code, placeholder: 'Code...' },
};

/* ─── Single Block Editor ───────────────────────────────────────── */
const BlockEditor = memo(function BlockEditor({
  block, onChange, onDelete, onAddBelow, onTypeChange,
  aiSuggestion, onAcceptAI, onFocus, isFocused,
}) {
  const inputRef = useRef(null);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const { placeholder } = BLOCK_TYPES[block.type] || BLOCK_TYPES.paragraph;

  const handleKey = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onAddBelow();
    }
    if (e.key === 'Backspace' && block.content === '') {
      e.preventDefault();
      onDelete();
    }
    // Tab accepts AI suggestion
    if (e.key === 'Tab' && aiSuggestion) {
      e.preventDefault();
      onAcceptAI();
    }
  }, [block.content, onAddBelow, onDelete, aiSuggestion, onAcceptAI]);

  const blockClass = {
    paragraph: 'text-sm text-zinc-200 leading-relaxed',
    heading1:  'text-xl font-bold text-white',
    heading2:  'text-base font-semibold text-zinc-100',
    bullet:    'text-sm text-zinc-200',
    checklist: 'text-sm text-zinc-200',
    code:      'text-xs font-mono text-emerald-400',
  }[block.type] || 'text-sm text-zinc-200';

  return (
    <div className="group relative flex items-start gap-2 py-0.5">
      {/* Type toggle */}
      <div className="relative mt-1.5 flex-shrink-0">
        <button
          onClick={() => setShowTypeMenu((v) => !v)}
          className="w-4 h-4 rounded opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity flex items-center justify-center"
          style={{ color: '#a78bfa' }}
          tabIndex={-1}
        >
          <div className="w-1 h-1 rounded-full bg-current" />
        </button>
        <AnimatePresence>
          {showTypeMenu && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="absolute left-0 top-6 z-50 rounded-xl border overflow-hidden shadow-xl"
              style={{ background: '#1a1a2e', borderColor: 'rgba(255,255,255,0.08)', minWidth: 140 }}
            >
              {Object.entries(BLOCK_TYPES).map(([type, { label, icon: Icon }]) => (
                <button
                  key={type}
                  onClick={() => { onTypeChange(type); setShowTypeMenu(false); }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs hover:bg-white/5 transition-colors text-left"
                  style={{ color: block.type === type ? '#a78bfa' : 'rgba(255,255,255,0.6)' }}
                >
                  <Icon className="w-3 h-3" />
                  {label}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Checklist checkbox */}
      {block.type === 'checklist' && (
        <button
          onClick={() => onChange({ ...block, checked: !block.checked })}
          className="mt-1.5 flex-shrink-0 w-4 h-4 rounded border transition-all"
          style={{
            background: block.checked ? '#7c3aed' : 'transparent',
            borderColor: block.checked ? '#7c3aed' : 'rgba(255,255,255,0.2)',
          }}
        >
          {block.checked && <div className="w-full h-full flex items-center justify-center text-white text-[8px]">✓</div>}
        </button>
      )}

      {/* Bullet dot */}
      {block.type === 'bullet' && (
        <div className="mt-2 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-violet-400 opacity-60" />
      )}

      {/* Input area */}
      <div className="flex-1 relative">
        {block.type === 'code' ? (
          <textarea
            ref={inputRef}
            value={block.content}
            onChange={(e) => onChange({ ...block, content: e.target.value })}
            onKeyDown={handleKey}
            onFocus={onFocus}
            placeholder={placeholder}
            className={`w-full bg-transparent outline-none resize-none ${blockClass}`}
            style={{
              minHeight: 60,
              background: 'rgba(0,0,0,0.3)',
              borderRadius: 8,
              padding: '8px 10px',
              border: isFocused ? '1px solid rgba(167,139,250,0.3)' : '1px solid rgba(255,255,255,0.05)',
            }}
            rows={3}
            spellCheck={false}
          />
        ) : (
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={block.content}
              onChange={(e) => onChange({ ...block, content: e.target.value })}
              onKeyDown={handleKey}
              onFocus={onFocus}
              placeholder={placeholder}
              className={`w-full bg-transparent outline-none ${blockClass} ${block.type === 'checklist' && block.checked ? 'line-through opacity-50' : ''}`}
              style={{ caretColor: '#a78bfa' }}
              spellCheck={block.type !== 'code'}
            />
            {/* AI ghost suggestion */}
            {isFocused && aiSuggestion && block.content && (
              <span
                className="pointer-events-none absolute top-0 left-0 text-sm"
                style={{ color: 'rgba(167,139,250,0.35)', whiteSpace: 'pre' }}
              >
                {block.content}
                <span style={{ color: 'rgba(167,139,250,0.5)' }}>{aiSuggestion}</span>
              </span>
            )}
          </div>
        )}
        {isFocused && aiSuggestion && (
          <div className="flex items-center gap-1 mt-0.5">
            <Sparkles className="w-2.5 h-2.5" style={{ color: '#a78bfa' }} />
            <span className="text-[10px]" style={{ color: 'rgba(167,139,250,0.6)' }}>
              Tab pour accepter
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

/* ─── Audio Waveform Visualizer ─────────────────────────────────── */
const AudioWave = memo(function AudioWave({ analyser }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!analyser || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const barWidth = (canvas.width / bufferLength) * 2.5;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height;
        const alpha = 0.4 + (dataArray[i] / 255) * 0.6;
        ctx.fillStyle = `rgba(139,92,246,${alpha})`;
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        x += barWidth + 1;
      }
    };
    draw();
    return () => cancelAnimationFrame(rafRef.current);
  }, [analyser]);

  return (
    <canvas
      ref={canvasRef}
      width={200}
      height={32}
      className="rounded"
      style={{ background: 'rgba(139,92,246,0.08)' }}
    />
  );
});

/* ─── Main BlockNotesView ───────────────────────────────────────── */
export default function BlockNotesView() {
  const { notes, loading, saveNote, deleteNote } = useBlockNotes();
  const notesListRef = useScrollPosition('notes-list');
  const [selectedNote, setSelectedNote] = useState(null);
  const [search, setSearch] = useState('');
  const [focusedBlockId, setFocusedBlockId] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState({});
  const [aiLoading, setAiLoading] = useState({});

  /* Audio state */
  const [audioRecording, setAudioRecording] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioElementRef = useRef(null);
  const [audioAnalyser, setAudioAnalyser] = useState(null);

  /* Video state */
  const [videoRecording, setVideoRecording] = useState(false);
  const videoChunksRef = useRef([]);
  const videoRecorderRef = useRef(null);
  const videoStreamRef = useRef(null);
  const liveVideoRef = useRef(null);

  /* Transcription */
  const recognitionRef = useRef(null);
  const [transcribing, setTranscribing] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');

  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  const aiDebounceRef = useRef({});

  /* ── Create a fresh note ── */
  const handleNewNote = useCallback(() => {
    const note = createNewNote({ title: '' });
    setSelectedNote(note);
    setLiveTranscript('');
    setAiSuggestions({});
  }, []);

  /* ── Save current note ── */
  const handleSave = useCallback(async (noteOverride) => {
    const note = noteOverride || selectedNote;
    if (!note) return;
    const saved = await saveNote(note);
    if (saved) setSelectedNote(saved);
  }, [selectedNote, saveNote]);

  /* ── Block operations ── */
  const updateBlock = useCallback((blockId, changes) => {
    setSelectedNote((prev) => {
      if (!prev) return prev;
      const blocks = prev.blocks.map((b) => b.id === blockId ? { ...b, ...changes } : b);
      const updated = { ...prev, blocks };
      handleSave(updated);
      return updated;
    });
  }, [handleSave]);

  const addBlockBelow = useCallback((afterId) => {
    setSelectedNote((prev) => {
      if (!prev) return prev;
      const idx = prev.blocks.findIndex((b) => b.id === afterId);
      const newBlock = {
        id: `b-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        type: 'paragraph',
        content: '',
      };
      const blocks = [
        ...prev.blocks.slice(0, idx + 1),
        newBlock,
        ...prev.blocks.slice(idx + 1),
      ];
      const updated = { ...prev, blocks };
      handleSave(updated);
      setFocusedBlockId(newBlock.id);
      return updated;
    });
  }, [handleSave]);

  const deleteBlock = useCallback((blockId) => {
    setSelectedNote((prev) => {
      if (!prev || prev.blocks.length <= 1) return prev;
      const blocks = prev.blocks.filter((b) => b.id !== blockId);
      const updated = { ...prev, blocks };
      handleSave(updated);
      return updated;
    });
  }, [handleSave]);

  const changeBlockType = useCallback((blockId, newType) => {
    setSelectedNote((prev) => {
      if (!prev) return prev;
      const blocks = prev.blocks.map((b) => b.id === blockId ? { ...b, type: newType } : b);
      const updated = { ...prev, blocks };
      handleSave(updated);
      return updated;
    });
  }, [handleSave]);

  /* ── AI Completion (debounced) ── */
  const requestAI = useCallback((blockId, text) => {
    if (!apiKey || text.length < 10) {
      setAiSuggestions((prev) => ({ ...prev, [blockId]: '' }));
      return;
    }
    clearTimeout(aiDebounceRef.current[blockId]);
    aiDebounceRef.current[blockId] = setTimeout(async () => {
      setAiLoading((prev) => ({ ...prev, [blockId]: true }));
      const suggestion = await getAICompletion(text, apiKey);
      setAiSuggestions((prev) => ({ ...prev, [blockId]: suggestion }));
      setAiLoading((prev) => ({ ...prev, [blockId]: false }));
    }, 800);
  }, [apiKey]);

  const handleBlockChange = useCallback((block) => {
    updateBlock(block.id, block);
    if (block.type === 'paragraph' || block.type === 'heading1' || block.type === 'heading2') {
      requestAI(block.id, block.content);
    }
  }, [updateBlock, requestAI]);

  const acceptAI = useCallback((blockId) => {
    const suggestion = aiSuggestions[blockId];
    if (!suggestion) return;
    setSelectedNote((prev) => {
      if (!prev) return prev;
      const blocks = prev.blocks.map((b) =>
        b.id === blockId ? { ...b, content: b.content + suggestion } : b
      );
      const updated = { ...prev, blocks };
      handleSave(updated);
      return updated;
    });
    setAiSuggestions((prev) => ({ ...prev, [blockId]: '' }));
  }, [aiSuggestions, handleSave]);

  /* ── Audio Recording ── */
  const startAudioRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];

      // Waveform analyser
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      const analyser = audioContextRef.current.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      setAudioAnalyser(analyser);

      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (e) => audioChunksRef.current.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = (ev) => {
          setSelectedNote((prev) => {
            if (!prev) return prev;
            const updated = { ...prev, audioData: ev.target.result };
            handleSave(updated);
            return updated;
          });
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach((t) => t.stop());
        if (audioContextRef.current) audioContextRef.current.close();
        setAudioAnalyser(null);
      };
      recorder.start(100);
      mediaRecorderRef.current = recorder;
      setAudioRecording(true);
    } catch (err) {
      console.error('Audio recording error:', err);
    }
  }, [handleSave]);

  const stopAudioRecording = useCallback(() => {
    if (mediaRecorderRef.current && audioRecording) {
      mediaRecorderRef.current.stop();
      setAudioRecording(false);
    }
  }, [audioRecording]);

  const playAudio = useCallback(() => {
    if (!selectedNote?.audioData) return;
    if (audioPlaying) {
      audioElementRef.current?.pause();
      setAudioPlaying(false);
      return;
    }
    const audio = new Audio(selectedNote.audioData);
    audioElementRef.current = audio;
    audio.onended = () => setAudioPlaying(false);
    audio.play();
    setAudioPlaying(true);
  }, [selectedNote, audioPlaying]);

  /* ── Video Recording ── */
  const startVideoRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      videoStreamRef.current = stream;
      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
        liveVideoRef.current.play();
      }
      videoChunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (e) => videoChunksRef.current.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(videoChunksRef.current, { type: 'video/webm' });
        const reader = new FileReader();
        reader.onload = (ev) => {
          setSelectedNote((prev) => {
            if (!prev) return prev;
            const updated = { ...prev, videoData: ev.target.result };
            handleSave(updated);
            return updated;
          });
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach((t) => t.stop());
        if (liveVideoRef.current) liveVideoRef.current.srcObject = null;
      };
      recorder.start(100);
      videoRecorderRef.current = recorder;
      setVideoRecording(true);
    } catch (err) {
      console.error('Video recording error:', err);
    }
  }, [handleSave]);

  const stopVideoRecording = useCallback(() => {
    if (videoRecorderRef.current && videoRecording) {
      videoRecorderRef.current.stop();
      setVideoRecording(false);
    }
  }, [videoRecording]);

  /* ── Real-time Speech Transcription ── */
  const startTranscription = useCallback(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('La reconnaissance vocale n\'est pas supportée dans ce navigateur.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let interim = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += transcript + ' ';
        } else {
          interim += transcript;
        }
      }
      if (finalText) {
        setSelectedNote((prev) => {
          if (!prev) return prev;
          const newTranscript = (prev.transcript || '') + finalText;
          const updated = { ...prev, transcript: newTranscript };
          handleSave(updated);
          return updated;
        });
      }
      setLiveTranscript(interim);
    };

    recognition.onend = () => {
      if (transcribing) recognition.start(); // restart if still active
    };

    recognition.onerror = (e) => {
      if (e.error !== 'no-speech') {
        console.error('Speech recognition error:', e.error);
        setTranscribing(false);
      }
    };

    recognition.start();
    recognitionRef.current = recognition;
    setTranscribing(true);
  }, [transcribing, handleSave]);

  const stopTranscription = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current?.abort();
    recognitionRef.current = null;
    setTranscribing(false);
    setLiveTranscript('');
  }, []);

  /* ── Cleanup ── */
  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
      videoStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  /* ── Title update ── */
  const handleTitleChange = useCallback((e) => {
    const title = e.target.value;
    setSelectedNote((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, title };
      handleSave(updated);
      return updated;
    });
  }, [handleSave]);

  /* ── Filtered notes ── */
  const filteredNotes = notes.filter((n) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      n.title?.toLowerCase().includes(q) ||
      n.blocks?.some((b) => b.content?.toLowerCase().includes(q)) ||
      n.transcript?.toLowerCase().includes(q)
    );
  });

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  /* ── RENDER ── */
  return (
    <div
      className="flex h-screen pt-[52px] pb-[88px] overflow-hidden"
      style={{ background: '#0e0e14' }}
    >
      {/* ─── Sidebar ─── */}
      <AnimatePresence mode="wait">
        {!selectedNote && (
          <motion.div
            key="sidebar"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex flex-col w-full overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b"
              style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <div>
                <h1 className="text-base font-bold text-white">Bloc-Notes</h1>
                <p className="text-[10px] text-zinc-500">{notes.length} note{notes.length !== 1 ? 's' : ''}</p>
              </div>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={handleNewNote}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold"
                style={{ background: 'rgba(139,92,246,0.15)', color: '#a78bfa' }}
              >
                <Plus className="w-3.5 h-3.5" />
                Nouvelle note
              </motion.button>
            </div>

            {/* Search */}
            <div className="px-3 py-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.04)' }}>
                <Search className="w-3 h-3 text-zinc-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher..."
                  className="flex-1 bg-transparent outline-none text-xs text-zinc-300 placeholder-zinc-600"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="text-zinc-500 hover:text-zinc-300">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Notes list */}
            <div ref={notesListRef} className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
                </div>
              )}
              {!loading && filteredNotes.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center py-12 gap-3"
                >
                  <FileText className="w-10 h-10 text-zinc-700" />
                  <p className="text-sm text-zinc-600">
                    {search ? 'Aucune note trouvée' : 'Aucune note pour l\'instant'}
                  </p>
                  {!search && (
                    <button
                      onClick={handleNewNote}
                      className="text-xs px-4 py-2 rounded-xl font-medium"
                      style={{ background: 'rgba(139,92,246,0.15)', color: '#a78bfa' }}
                    >
                      Créer ma première note
                    </button>
                  )}
                </motion.div>
              )}
              {filteredNotes.map((note) => (
                <motion.button
                  key={note.id}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => { setSelectedNote(note); setLiveTranscript(''); }}
                  className="w-full text-left px-3 py-2.5 rounded-xl transition-colors"
                  style={{ background: 'rgba(255,255,255,0.03)' }}
                  whileHover={{ background: 'rgba(139,92,246,0.08)' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-200 truncate">
                        {note.title || 'Note sans titre'}
                      </p>
                      <p className="text-[10px] text-zinc-600 mt-0.5 line-clamp-1">
                        {note.blocks?.[0]?.content || note.transcript || '—'}
                      </p>
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1">
                      <span className="text-[9px] text-zinc-600">{formatDate(note.updatedAt)}</span>
                      <div className="flex gap-1">
                        {note.audioData && <Volume2 className="w-2.5 h-2.5 text-violet-400" />}
                        {note.videoData && <Video className="w-2.5 h-2.5 text-violet-400" />}
                        {note.transcript && <FileText className="w-2.5 h-2.5 text-violet-400" />}
                      </div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* ─── Editor Panel ─── */}
        {selectedNote && (
          <motion.div
            key="editor"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="flex flex-col w-full overflow-hidden"
          >
            {/* Editor header */}
            <div
              className="flex items-center gap-2 px-3 py-2 border-b flex-shrink-0"
              style={{ borderColor: 'rgba(255,255,255,0.06)' }}
            >
              <button
                onClick={() => { setSelectedNote(null); stopTranscription(); stopAudioRecording(); stopVideoRecording(); }}
                className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <input
                type="text"
                value={selectedNote.title}
                onChange={handleTitleChange}
                placeholder="Titre de la note..."
                className="flex-1 bg-transparent outline-none text-sm font-semibold text-white placeholder-zinc-600"
              />

              {/* Recording controls */}
              <div className="flex items-center gap-1">
                {/* Transcription */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={transcribing ? stopTranscription : startTranscription}
                  className="p-1.5 rounded-lg transition-all"
                  style={{
                    background: transcribing ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.05)',
                    color: transcribing ? '#f87171' : 'rgba(255,255,255,0.4)',
                  }}
                  title={transcribing ? 'Arrêter la transcription' : 'Transcrire la parole'}
                >
                  {transcribing
                    ? <MicOff className="w-3.5 h-3.5" />
                    : <FileText className="w-3.5 h-3.5" />
                  }
                </motion.button>

                {/* Audio */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={audioRecording ? stopAudioRecording : startAudioRecording}
                  className="p-1.5 rounded-lg transition-all"
                  style={{
                    background: audioRecording ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.05)',
                    color: audioRecording ? '#f87171' : 'rgba(255,255,255,0.4)',
                  }}
                  title={audioRecording ? 'Arrêter l\'audio' : 'Enregistrer l\'audio'}
                >
                  {audioRecording ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                </motion.button>

                {/* Video */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={videoRecording ? stopVideoRecording : startVideoRecording}
                  className="p-1.5 rounded-lg transition-all"
                  style={{
                    background: videoRecording ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.05)',
                    color: videoRecording ? '#f87171' : 'rgba(255,255,255,0.4)',
                  }}
                  title={videoRecording ? 'Arrêter la vidéo' : 'Enregistrer la vidéo'}
                >
                  {videoRecording ? <VideoOff className="w-3.5 h-3.5" /> : <Video className="w-3.5 h-3.5" />}
                </motion.button>

                {/* Delete */}
                <button
                  onClick={async () => {
                    await deleteNote(selectedNote.id);
                    setSelectedNote(null);
                  }}
                  className="p-1.5 rounded-lg text-zinc-600 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Recording status banner */}
            <AnimatePresence>
              {(audioRecording || videoRecording || transcribing) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden flex-shrink-0"
                >
                  <div
                    className="flex items-center gap-3 px-4 py-2"
                    style={{ background: 'rgba(239,68,68,0.08)', borderBottom: '1px solid rgba(239,68,68,0.12)' }}
                  >
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    {audioRecording && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-red-400 font-medium">Audio</span>
                        {audioAnalyser && <AudioWave analyser={audioAnalyser} />}
                      </div>
                    )}
                    {videoRecording && (
                      <span className="text-[10px] text-red-400 font-medium">Vidéo en cours...</span>
                    )}
                    {transcribing && (
                      <span className="text-[10px] text-red-400 font-medium">Transcription active</span>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Live video preview */}
            <AnimatePresence>
              {videoRecording && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  className="overflow-hidden flex-shrink-0 bg-black"
                >
                  <video
                    ref={liveVideoRef}
                    muted
                    playsInline
                    className="w-full max-h-32 object-cover"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Editor body */}
            <div className="flex-1 overflow-y-auto">
              {/* Blocks */}
              <div className="px-4 py-3 space-y-0.5">
                {selectedNote.blocks.map((block) => (
                  <BlockEditor
                    key={block.id}
                    block={block}
                    onChange={handleBlockChange}
                    onDelete={() => deleteBlock(block.id)}
                    onAddBelow={() => addBlockBelow(block.id)}
                    onTypeChange={(type) => changeBlockType(block.id, type)}
                    aiSuggestion={aiSuggestions[block.id] || ''}
                    onAcceptAI={() => acceptAI(block.id)}
                    onFocus={() => setFocusedBlockId(block.id)}
                    isFocused={focusedBlockId === block.id}
                  />
                ))}
                {/* Add block button */}
                <button
                  onClick={() => addBlockBelow(selectedNote.blocks[selectedNote.blocks.length - 1]?.id)}
                  className="flex items-center gap-1.5 mt-2 text-[11px] text-zinc-700 hover:text-zinc-500 transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  Ajouter un bloc
                </button>
              </div>

              {/* Live transcript display */}
              <AnimatePresence>
                {(transcribing || selectedNote.transcript) && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mx-4 mb-3 rounded-xl p-3"
                    style={{ background: 'rgba(139,92,246,0.07)', border: '1px solid rgba(139,92,246,0.12)' }}
                  >
                    <div className="flex items-center gap-1.5 mb-2">
                      <Mic className="w-3 h-3 text-violet-400" />
                      <span className="text-[10px] font-semibold text-violet-400">Transcription</span>
                      {transcribing && (
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                      )}
                    </div>
                    <p className="text-xs text-zinc-300 leading-relaxed">
                      {selectedNote.transcript}
                      {liveTranscript && (
                        <span style={{ color: 'rgba(167,139,250,0.6)' }}>{liveTranscript}</span>
                      )}
                    </p>
                    {selectedNote.transcript && (
                      <button
                        onClick={() => {
                          const lastBlock = selectedNote.blocks[selectedNote.blocks.length - 1];
                          const newContent = (lastBlock.content ? lastBlock.content + '\n' : '') + selectedNote.transcript;
                          updateBlock(lastBlock.id, { content: newContent });
                          setSelectedNote((prev) => ({ ...prev, transcript: '' }));
                        }}
                        className="mt-2 text-[10px] text-violet-400 hover:text-violet-300 transition-colors"
                      >
                        Insérer dans la note →
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Audio playback */}
              <AnimatePresence>
                {selectedNote.audioData && !audioRecording && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mx-4 mb-3 flex items-center gap-3 rounded-xl px-3 py-2.5"
                    style={{ background: 'rgba(139,92,246,0.07)', border: '1px solid rgba(139,92,246,0.12)' }}
                  >
                    <button
                      onClick={playAudio}
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(139,92,246,0.2)' }}
                    >
                      {audioPlaying
                        ? <Pause className="w-3 h-3 text-violet-400" />
                        : <Play className="w-3 h-3 text-violet-400" />
                      }
                    </button>
                    <div className="flex-1">
                      <p className="text-[10px] text-violet-400 font-medium">Note audio</p>
                      <div className="mt-1 h-1 rounded-full bg-violet-900/50" />
                    </div>
                    <Volume2 className="w-3.5 h-3.5 text-zinc-600" />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Video playback */}
              <AnimatePresence>
                {selectedNote.videoData && !videoRecording && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mx-4 mb-3 rounded-xl overflow-hidden"
                    style={{ border: '1px solid rgba(139,92,246,0.12)' }}
                  >
                    <video
                      src={selectedNote.videoData}
                      controls
                      playsInline
                      className="w-full max-h-48 bg-black"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* AI status footer */}
            <AnimatePresence>
              {Object.values(aiLoading).some(Boolean) && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-1.5 px-4 py-1.5 flex-shrink-0"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
                >
                  <Sparkles className="w-3 h-3 text-violet-400 animate-pulse" />
                  <span className="text-[10px] text-zinc-600">IA en train de compléter...</span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
